import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ThumbsUp, ThumbsDown, BookPlus, BookMinus, Check, RotateCcw, AlertTriangle } from 'lucide-react';

import { aiApi } from '../../../api/ai.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { KpiCard } from '../../../components/ui/KpiCard.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Tabs } from '../../../components/ui/Tabs.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { MarkdownEditor } from '../../../components/ui/MarkdownEditor.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { FeedbackDocs } from './FeedbackDocs.jsx';
import { fmt } from '../../../lib/formatters.js';

const VIEW_TABS = [
  { value: 'review', label: 'Revisione' },
  { value: 'docs', label: 'Documenti integrati' }
];

const THUMB_TABS = [
  { value: '', label: 'Tutti' },
  { value: 'down', label: 'Negativi' },
  { value: 'up', label: 'Positivi' }
];

const AGENT_OPTS = [
  { value: '', label: 'Tutti gli agenti' },
  { value: 'orchestrator', label: 'Orchestratore' },
  { value: 'customer', label: 'Agente Cliente' },
  { value: 'inventory', label: 'Agente Inventario' },
  { value: 'consultant', label: 'Agente Consulente' },
  { value: 'procedure', label: 'Agente Procedure' }
];

function PromoteModal({ open, item, onClose, onConfirm, loading }) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');

  const isNegative = item && item.feedback && item.feedback.thumbs === 'down';
  const hasCorrection = item && item.feedback && item.feedback.correction;
  // A negative feedback with no written correction is risky to promote: its
  // pre-filled answer is the one that was disliked. We must not seed the index
  // with it, so we seed a placeholder and warn the operator to write the right
  // answer first.
  const negativeNoCorrection = isNegative && !hasCorrection;

  // Seed the fields once when the modal opens for a new item.
  if (open && item && title === '' && text === '') {
    const seedTitle = (item.question || 'Correzione da feedback').slice(0, 80);
    let answerLine;
    if (hasCorrection) answerLine = `Risposta corretta: ${item.feedback.correction}`;
    else if (negativeNoCorrection) answerLine = 'Risposta corretta: ';      // operator fills it
    else answerLine = `Risposta: ${item.answer || ''}`;                     // positive: good answer
    const seedText = [
      item.question ? `Domanda: ${item.question}` : null,
      answerLine
    ].filter(Boolean).join('\n\n');
    setTitle(seedTitle);
    setText(seedText);
  }

  const close = () => { setTitle(''); setText(''); onClose(); };

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : close}
      title="Aggiungi alla Knowledge base"
      description="Questo crea un documento indicizzato dal RAG: l'assistente lo userà nelle risposte future."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={loading}>Annulla</Button>
          <Button onClick={() => onConfirm({ title, text })} loading={loading} disabled={!text.trim()}>
            <BookPlus className="h-4 w-4" /> Crea documento
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {negativeNoCorrection && (
          <p className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-ink">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <span>
              Feedback <strong>negativo senza correzione</strong>: scrivi tu la <strong>risposta corretta</strong>.
              Non inserire la risposta sbagliata, altrimenti l'assistente potrebbe ripeterla.
            </span>
          </p>
        )}
        <Input label="Titolo" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-ink">Contenuto</span>
          <MarkdownEditor minHeight={260} value={text} onChange={setText} />
        </div>
      </div>
    </Modal>
  );
}

export function FeedbackPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canRead = hasPermission('analytics:read');
  const canPromote = hasPermission('rag:upload');
  const canRevert = hasPermission('rag:delete');

  const [view, setView] = useState('review');           // 'review' | 'docs'
  const [promoting, setPromoting] = useState(null);     // feedback item being promoted
  const [unpromoting, setUnpromoting] = useState(null); // feedback item being reverted

  const statsQuery = useQuery({ queryKey: ['ai', 'feedback', 'stats'], enabled: canRead, queryFn: aiApi.feedbackStats });
  const stats = statsQuery.data || { up: 0, down: 0, unresolvedDown: 0, byAgent: [] };

  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['ai', 'feedback', 'list'],
    fetcher: aiApi.feedbackList,
    initialFilters: { thumbs: 'down', agentId: '', resolved: '' }
  });

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['ai', 'feedback'] });
  };

  const promoteMutation = useMutation({
    mutationFn: ({ messageId, body }) => aiApi.feedbackPromote(messageId, body),
    onSuccess: () => { setPromoting(null); refreshAll(); toast.success('Documento creato nella Knowledge base.'); },
    onError: (err) => toast.error(err.message || 'Operazione non riuscita.')
  });

  const unpromoteMutation = useMutation({
    mutationFn: (messageId) => aiApi.feedbackUnpromote(messageId),
    onSuccess: () => { setUnpromoting(null); refreshAll(); toast.success('Documento rimosso dalla Knowledge base.'); },
    onError: (err) => toast.error(err.message || 'Operazione non riuscita.')
  });

  const resolveMutation = useMutation({
    mutationFn: ({ messageId, resolved }) => aiApi.feedbackResolve(messageId, resolved),
    onSuccess: () => { refreshAll(); },
    onError: (err) => toast.error(err.message || 'Operazione non riuscita.')
  });

  if (!canRead) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Feedback AI" />
        <Card><p className="text-sm text-ink-muted">Permesso <code>analytics:read</code> richiesto.</p></Card>
      </div>
    );
  }

  const maxAgentTotal = Math.max(1, ...stats.byAgent.map((a) => a.up + a.down));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Feedback AI"
        description="Rivedi i giudizi sulle risposte dell'assistente e trasforma le correzioni in conoscenza."
        actions={<Tabs value={view} onChange={setView} items={VIEW_TABS} />}
      />

      {view === 'docs' ? <FeedbackDocs /> : (
      <>
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <KpiCard label="Positivi" value={fmt.int(stats.up)} />
        <KpiCard label="Negativi" value={fmt.int(stats.down)} />
        <KpiCard label="Negativi da gestire" value={fmt.int(stats.unresolvedDown)} />
      </section>

      <Card>
        <h2 className="mb-3 text-sm font-semibold text-ink">Gradimento per agente</h2>
        {statsQuery.isLoading ? (
          <div className="flex flex-col gap-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}</div>
        ) : stats.byAgent.length === 0 ? (
          <p className="text-sm text-ink-muted">Nessun feedback ancora registrato.</p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {stats.byAgent.map((a) => {
              const total = a.up + a.down;
              const ratio = total ? Math.round((a.up / total) * 100) : 0;
              return (
                <li key={a.agentId || 'unknown'} className="flex items-center gap-3 text-sm">
                  <span className="w-40 shrink-0 truncate text-ink">{a.agentLabel}</span>
                  <div className="flex h-2 flex-1 overflow-hidden rounded-full bg-surface-subtle" style={{ maxWidth: `${(total / maxAgentTotal) * 100}%` }}>
                    <div className="h-full bg-success" style={{ width: `${ratio}%` }} />
                    <div className="h-full bg-danger" style={{ width: `${100 - ratio}%` }} />
                  </div>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-ink-muted">
                    <span className="text-success">{a.up}</span>/<span className="text-danger">{a.down}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <Tabs value={filters.thumbs || ''} onChange={(v) => setFilters({ thumbs: v })} items={THUMB_TABS} />
        <div className="flex items-end gap-3">
          <Select label="Agente" className="w-48" value={filters.agentId || ''} onChange={(e) => setFilters({ agentId: e.target.value })}>
            {AGENT_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <Select label="Stato" className="w-40" value={filters.resolved || ''} onChange={(e) => setFilters({ resolved: e.target.value })}>
            <option value="">Tutti</option>
            <option value="false">Da gestire</option>
            <option value="true">Gestiti</option>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}</div>
      ) : items.length === 0 ? (
        <Card><p className="text-sm text-ink-muted">Nessun feedback con questi filtri.</p></Card>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((it) => {
            const down = it.feedback && it.feedback.thumbs === 'down';
            const resolved = Boolean(it.feedback && it.feedback.resolvedAt);
            const promoted = Boolean(it.feedback && it.feedback.promotedRagId);
            return (
              <li key={it.id}>
                <Card className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={down ? 'text-danger' : 'text-success'}>
                      {down ? <ThumbsDown className="h-4 w-4" /> : <ThumbsUp className="h-4 w-4" />}
                    </span>
                    <Badge tone="neutral">{it.agentLabel}</Badge>
                    {promoted && <Badge tone="success">In Knowledge base</Badge>}
                    {resolved && !promoted && <Badge tone="neutral">Gestito</Badge>}
                    <span className="ml-auto text-xs text-ink-faint">{fmt.dateTime(it.feedback && it.feedback.at)}</span>
                  </div>

                  {it.question && (
                    <p className="text-sm text-ink"><span className="text-ink-muted">Domanda:</span> {it.question}</p>
                  )}
                  <p className="whitespace-pre-wrap text-sm text-ink-muted">{it.answer}</p>
                  {it.feedback && it.feedback.correction && (
                    <p className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-sm text-ink">
                      <span className="font-medium text-warning">Correzione:</span> {it.feedback.correction}
                    </p>
                  )}

                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {canPromote && !promoted && (
                      <Button size="sm" variant="secondary" onClick={() => setPromoting(it)}>
                        <BookPlus className="h-3.5 w-3.5" /> Aggiungi a Knowledge base
                      </Button>
                    )}
                    {promoted && canRevert && (
                      <Button size="sm" variant="ghost" onClick={() => setUnpromoting(it)}>
                        <BookMinus className="h-3.5 w-3.5" /> Rimuovi da Knowledge base
                      </Button>
                    )}
                    {!resolved ? (
                      <Button size="sm" variant="ghost" onClick={() => resolveMutation.mutate({ messageId: it.id, resolved: true })} loading={resolveMutation.isPending}>
                        <Check className="h-3.5 w-3.5" /> Segna gestito
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => resolveMutation.mutate({ messageId: it.id, resolved: false })}>
                        <RotateCcw className="h-3.5 w-3.5" /> Riapri
                      </Button>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <Pagination pagination={pagination} onPageChange={setPage} />

      <PromoteModal
        open={Boolean(promoting)}
        item={promoting}
        loading={promoteMutation.isPending}
        onClose={() => setPromoting(null)}
        onConfirm={({ title, text }) => promoteMutation.mutate({ messageId: promoting.id, body: { title, text } })}
      />

      <ConfirmDialog
        open={Boolean(unpromoting)}
        destructive
        title="Rimuovi da Knowledge base"
        description="Il documento creato da questo feedback verrà eliminato dalla Knowledge base e il feedback tornerà tra quelli da gestire."
        confirmLabel="Rimuovi"
        loading={unpromoteMutation.isPending}
        onCancel={() => setUnpromoting(null)}
        onConfirm={() => unpromoteMutation.mutate(unpromoting.id)}
      />
      </>
      )}
    </div>
  );
}
